import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import SilentEvalCatalogDuplicates from './SilentEvalCatalogDuplicates';
import SilentEvalCreationRequests from './SilentEvalCreationRequests';
import SilentEvalLiveMatches from './SilentEvalLiveMatches';
import SilentEvalOpportunities from './SilentEvalOpportunities';
import SilentEvalReadiness from './SilentEvalReadiness';
import { useSearchParamsState } from '../../hooks/useSearchParamsState';

import './PinMatchSilentEval.scss';

const SEARCH_DEBOUNCE_MS = 300;
const TABS = [
  { id: 'queue', label: 'Queue', counted: true },
  { id: 'activity', label: 'Activity', counted: true },
  { id: 'duplicates', label: 'Duplicates', counted: true },
  { id: 'opportunities', label: 'Opportunities' },
  { id: 'create', label: 'Create requests' },
  { id: 'readiness', label: 'Report' }
];
const LIVE_TABS = ['queue', 'activity'];
const URL_DEFAULTS = { tab: 'queue', q: '' };
// Counts are compacted to at most four characters (999, 9.9k, 999k, 1m) so the badge stays tiny;
// a trailing "+" (activity has more pages) is kept.
const compactCount = n => {
  if (n < 1000) return String(n);
  const [size, unit] = n < 999500 ? [1000, 'k'] : [1000000, 'm'];
  const tenths = Math.round(n / (size / 10));
  return (tenths < 100 ? tenths / 10 : Math.round(n / size)) + unit;
};
export const formatTabCount = count => {
  const [, digits, plus] = String(count ?? '').match(/^(\d+)(\+?)$/) || [];
  return digits ? compactCount(Number(digits)) + plus : '';
};

// Panes stay mounted once opened (hidden, not unmounted), so coming back shows the last result
// instantly while the pane refreshes it in the background.
const PinMatchSilentEval = () => {
  const [urlState, patchUrl] = useSearchParamsState(URL_DEFAULTS);
  const searchInputRef = useRef(null);
  const activeTab = TABS.some(tab => tab.id === urlState.tab) ? urlState.tab : URL_DEFAULTS.tab;
  const search = urlState.q;
  const [liveMode, setLiveMode] = useState(LIVE_TABS.includes(activeTab) ? activeTab : 'queue');
  // Duplicates mounts hidden from the start so its tab count is there on page load.
  const [openedPanes, setOpenedPanes] = useState({ duplicates: true, [activeTab]: true });
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [liveCounts, setLiveCounts] = useState({});
  const [duplicateCount, setDuplicateCount] = useState(null);
  const counts = { ...liveCounts, duplicates: duplicateCount };
  const isLive = LIVE_TABS.includes(activeTab);
  const panes = [
    { id: 'duplicates', render: isActive => <SilentEvalCatalogDuplicates active={isActive} onCount={setDuplicateCount} /> },
    { id: 'opportunities', render: isActive => <SilentEvalOpportunities active={isActive} /> },
    { id: 'create', render: isActive => <SilentEvalCreationRequests active={isActive} /> },
    { id: 'readiness', render: isActive => <SilentEvalReadiness active={isActive} /> }
  ];

  const selectTab = id => {
    patchUrl({ tab: id });
    if (LIVE_TABS.includes(id)) setLiveMode(id);
    else setOpenedPanes(current => ({ ...current, [id]: true }));
  };

  const clearSearch = () => {
    patchUrl({ q: '' });
    searchInputRef.current.focus();
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <div className='silent-eval-cockpit'>
      <nav className='se-tabbar'>
        <div className='se-tabs' role='tablist' aria-label='Pin match review sections'>
          {TABS.map(tab => (
            <button
              key={tab.id}
              type='button'
              role='tab'
              aria-selected={activeTab === tab.id}
              className={cn('se-tab', { active: activeTab === tab.id })}
              onClick={() => selectTab(tab.id)}
            >
              <span className='se-tab-label'>{tab.label}</span>
              {tab.counted && counts[tab.id] != null && <span className='se-tab-count'>{formatTabCount(counts[tab.id])}</span>}
            </button>
          ))}
        </div>
        <div className='se-search-container'>
          <input
            ref={searchInputRef}
            type='search'
            className={cn('se-search', { hidden: !isLive })}
            value={search}
            placeholder='Search retailer, link or batch tag'
            aria-label='Search live matches'
            aria-hidden={!isLive}
            tabIndex={isLive ? 0 : -1}
            onChange={e => patchUrl({ q: e.target.value })}
          />
          {isLive && search && (
            <button type='button' className='se-search-clear' aria-label='Clear search' title='Clear search' onClick={clearSearch}>
              ×
            </button>
          )}
        </div>
      </nav>

      <div className='se-pane' hidden={!isLive}>
        <SilentEvalLiveMatches mode={liveMode} active={isLive} search={debouncedSearch} onCounts={setLiveCounts} onTagClick={tag => patchUrl({ q: tag })} />
      </div>
      {panes
        .filter(pane => openedPanes[pane.id])
        .map(pane => (
          <div key={pane.id} className='se-pane' hidden={activeTab !== pane.id}>
            {pane.render(activeTab === pane.id)}
          </div>
        ))}
    </div>
  );
};

export default PinMatchSilentEval;
